import { Button } from "@/components/ui/button"
import {
    Drawer,
    DrawerContent,
    DrawerTitle,
    DrawerTrigger
} from "@/components/ui/drawer"
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AcessoService, { PresenteadoDto } from "@/services/AcessoService";
import { Label } from "@/components/ui/label";
import * as Yup from 'yup';
import { useFormik } from "formik";
import CidadesService, { CidadeDto } from "@/services/CidadesService";
import Select from 'react-select';
import { InputMask } from "@/components/ui/input-mask";
import { MASCARA_CEP, MASCARA_CPF, MASCARA_TELEFONE } from "@/utils/Constants";
import { EnderecoDto } from "@/dtos/EnderedoDto";
import { InputFormik } from "@/components/ui/input-formik";
import CompraRapidaService from "@/services/CompraRapidaService";
import EnderecoService from "@/services/EnderecoService";
import { useToast } from "@/components/ui/use-toast";
import { X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import clsx from "clsx";
import AppService from "@/services/AppService";
import { normalizarCPF, validarCPF } from "@/utils/Cpf";
import { validarNomeCompleto } from "@/utils/NomeCompleto";

interface DrawerPagamentoParaOutraPessoaProps {
    presenteados: PresenteadoDto[],
    handlePresenteados: (action: 'remove' | 'load', presenteado?: PresenteadoDto) => void,
}

export function DrawerPagamentoParaOutraPessoa(props: DrawerPagamentoParaOutraPessoaProps) {
    const [step, setStep] = useState<'selecionar' | 'pesquisarcpf' | 'cadastrar' | 'confirmar'>('selecionar')
    const [exibirDrawer, setExibirDrawer] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams();
    const [endereco, setEndereco] = useState<EnderecoDto>();
    const [cidades, setCidades] = useState<CidadeDto[]>([]);
    const cidadesSelect = [{ value: '', label: 'Selecione sua Cidade' }, ...cidades.map(cidade => ({ value: cidade.cidade, label: cidade.cidade }))];
    const [buscandoPessoa, setBuscandoPessoa] = useState(false);
    const [deletandoPessoa, setDeletandoPessoa] = useState(0);
    const { toast } = useToast();
    const [presenteado, setPresenteado] = useState<PresenteadoDto>();
    const [token, setToken] = useState('')
    const navigate = useNavigate();

    useEffect(() => {
        if (location.pathname.toLowerCase().indexOf('/finalizarcompraapp') >= 0) {
            const searchParamsRealizandoCadastro = searchParams.get('token')

            if (searchParamsRealizandoCadastro !== null) {
                setToken(searchParamsRealizandoCadastro);
            } else {
                navigate('/')
            }
        }
    }, [])

    const formContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleResize = () => {
            if (formContainerRef.current) {
                formContainerRef.current.style.setProperty('bottom', `env(safe-area-inset-bottom)`);
            }
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", handleResize);
            handleResize(); // Initial call in case the keyboard is already open
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", handleResize);
            }
        };
    }, []);

    async function handleSelecionarPessoa(id: number) {
        const presenteado = props.presenteados.find(c => c.IdUsuario === id);
        const searchParamToken = searchParams.get('token')
        setSearchParams(
            {
                token: searchParamToken,
                idPessoaPresenteada: id
            } as any
        );

        setPresenteado(presenteado);
        setExibirDrawer(false)
    }

    async function handleRemoverPessoaSelecionada() {
        setPresenteado(undefined);
        const searchParamToken = searchParams.get('token')
        setSearchParams(
            {
                token: searchParamToken,
                idPessoaPresenteada: ''
            } as any
        );
    }

    async function handleRemoverVinculoPessoa(id: number) {
        if (deletandoPessoa > 0) {
            return;
        }

        setDeletandoPessoa(id);
        const searchParamToken = searchParams.get('token')
        setSearchParams(
            {
                token: searchParamToken,
                idPessoaPresenteada: ''
            } as any
        );
        await props.handlePresenteados('remove',
            {
                IdUsuario: id,
                CPF: '',
                Nome: '',
                Endereco: {} as EnderecoDto,
                Telefone: '',
            }
        )
        setPresenteado(undefined);
        setDeletandoPessoa(0);
    }

    useEffect(() => {
        async function buscarCidades() {
            const cidadess = await CidadesService.buscarCidades();
            setCidades(cidadess);
        }

        buscarCidades()
    }, [])

    useEffect(() => {
        const idPessoaPresenteada = Number(searchParams.get('idPessoaPresenteada') ?? 0);
        if (idPessoaPresenteada > 0) {
            const presenteada = props.presenteados.find(c => c.IdUsuario === idPessoaPresenteada);
            setPresenteado(presenteada);
        }
    }, [])

    function handleBotaoVoltarSelecao() {
        setStep('selecionar')
        setEndereco(undefined);
        formik.resetForm();
    }

    const formik = useFormik({
        initialValues: {
            IdPessoaPresenteada: 0,
            CPF: '',
            Nome: '',
            Telefone: '',
            Email: '',
            CEP: '',
        },
        validationSchema: Yup.object({
            Nome: Yup.string()
                .required('Obrigatório')
                .test('Validar Nome Completo', 'Informe o nome completo', validarNomeCompleto),
            CPF: Yup.string()
                .required('Obrigatório')
                .test('Validar CPF', 'CPF Inválido', cpfToValidate => {
                    return validarCPF(cpfToValidate || '');
                }),
            Telefone: Yup.string()
                .required('Obrigatório')
                .test('Validar Telefone', 'Telefone Inválido', telefoneToValidate => {
                    if (telefoneToValidate
                        .replace(/_/g, '')
                        .replace(/\(/g, '')
                        .replace(/\)/g, '')
                        .replace(/-/g, '')
                        .replace(/\./g, '')
                        .replace(/\s/g, '')
                        .length !== 11) {
                        return false;
                    }

                    return true;
                }),
            Email: Yup.string().email('Digite um email Válido'),
            CEP: Yup.string()
        }),
        onSubmit: async (values) => {
            const dados = {
                idUsuario: 0,
                CPF: normalizarCPF(values.CPF),
                Nome: values.Nome,
                Endereco: endereco!,
                Telefone: values.Telefone.replace(/_/g, '')
                    .replace(/\(/g, '')
                    .replace(/\)/g, '')
                    .replace(/-/g, '')
                    .replace(/\./g, '')
                    .replace(/\s/g, ''),
            }

            if (token) {
                await AppService.adicionarPresenteado(dados, token);
            } else {
                await AcessoService.adicionarPresenteado(dados)
            }

            await props.handlePresenteados('load');
            await handleBotaoVoltarSelecao()
        }
    })

    async function BuscarPorCep(cep: string) {
        try {
            const { Data: result } = await EnderecoService.buscarPorCep(cep);
            setEndereco(result);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        }
    }

    useEffect(() => {
        const cep = formik.values.CEP.replace(/_/g, '').replace(/-/g, '');

        if (formik.values.CEP.replace(/_/g, '').replace(/-/g, '').length !== 8) {
            return;
        }

        setEndereco(undefined);
        BuscarPorCep(cep);
    }, [formik.values.CEP])

    async function verificarRecaptcha() {
        try {
            const grecaptcha = (window as any).grecaptcha;
            const tokenRecaptcha = await grecaptcha.execute('6LdL-BcsAAAAAMp9u6pw0p90qR518KgqzfiSihU3', { action: 'submit' })!;
            return tokenRecaptcha;
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
            return null;
        }
    }

    useEffect(() => {
        async function buscarCadastroPorCpf() {
            try {
                const cpf = normalizarCPF(formik.values.CPF);

                if (cpf.length !== 11) {
                    return;
                }

                if (!validarCPF(cpf)) {
                    return;
                }

                setBuscandoPessoa(true);

                const recapchaToken = await verificarRecaptcha();
                if (!recapchaToken) {
                    setBuscandoPessoa(false);
                    return;
                }

                const { Data: result } = await CompraRapidaService.buscarClientePorCPF(cpf, recapchaToken, token);

                // setUsuario(result);
                formik.setValues(
                    {
                        ...formik.values,
                        Nome: result.Nome,
                        Telefone: result.Telefone,
                        Email: result.Email
                    })

                if (result.idUsuario > 0) {
                    try {
                        const dados = {
                            idUsuario: result.idUsuario,
                            CPF: '',
                            Nome: '',
                            Endereco: {} as EnderecoDto,
                            Telefone: '',
                        };

                        if (token) {
                            await AppService.adicionarPresenteado(dados, token)
                        } else {
                            await AcessoService.adicionarPresenteado(dados)
                        }


                        await props.handlePresenteados('load');
                        await handleBotaoVoltarSelecao()
                    } catch (error) {

                    }
                } else {
                    setStep('cadastrar')
                }
            } catch (error: any) {
                if (error === 'Não encontrado. Preencha os dados manualmente.') {
                    // setUsuario(undefined);
                    formik.setValues({ ...formik.values, Nome: '', Email: '', Telefone: '' })
                    setStep('cadastrar')
                    return;
                }
                
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                });
            } finally {
                setBuscandoPessoa(false);
            }
        }

        buscarCadastroPorCpf();
    }, [formik.values.CPF])

    return (
        <div className=" mt-6">
            {
                presenteado && (
                    <div className="flex justify-center gap-3 p-3">
                        <span className="text-lg text-center">Você está comprando para<br />
                            <span className="font-bold">
                                {presenteado.Nome}
                            </span>
                        </span>
                        <span className="flex justify-start items-center self-center" onClick={handleRemoverPessoaSelecionada} title="Remover" >
                            <X className="text-destructive cursor-pointer" size={40} />
                        </span>
                    </div>
                )
            }
            <Drawer direction="right" open={exibirDrawer} onOpenChange={setExibirDrawer}>
                <DrawerTitle className="hidden">Pagamento para outra pessoa</DrawerTitle>
                <DrawerTrigger onClick={() => setExibirDrawer(true)} className="flex justify-center items-center cursor-pointer" asChild>
                    <Button className="w-[90%] mx-auto mt-4">
                        Comprando para outra pessoa?
                    </Button>
                </DrawerTrigger>
                <DrawerContent ref={formContainerRef} className="min-h-[70vh]">
                    <ScrollArea>
                        <div className="flex flex-col items-center justify-center mb-10">
                            <h1 className="text-lg text-primary font-bold">Selecione ou Cadastre uma Pessoa</h1>
                            {
                                step === 'selecionar' &&
                                <Button className="mt-3" onClick={() => setStep('pesquisarcpf')}>Cadastrar Pessoa</Button>
                            }
                            {
                                step === 'selecionar' &&
                                <div className="flex flex-col justify-center gap-2 mt-7">
                                    {
                                        props.presenteados.map(presenteado => (
                                            <div
                                                className={clsx("flex justify-between gap-10 bg-primary p-2 rounded-xl",
                                                    {
                                                        "hover:bg-primary/60": deletandoPessoa === presenteado.IdUsuario
                                                    }
                                                )}
                                                key={presenteado.IdUsuario}>
                                                <span
                                                    onClick={() => handleSelecionarPessoa(presenteado.IdUsuario)}
                                                    className="text-lg cursor-pointer hover:underline">
                                                    {presenteado.Nome}
                                                </span>

                                                <div
                                                    onClick={() => handleRemoverVinculoPessoa(presenteado.IdUsuario)}
                                                    className="justify-center cursor-pointer items-center self-center">
                                                    {
                                                        deletandoPessoa === presenteado.IdUsuario ? <>Removendo...</> : <X />
                                                    }
                                                </div>
                                                {/* <span
                                                onClick={() => handleRemoverVinculoPessoa(presenteado.IdUsuario)}
                                                className="text-destructive hover:underline cursor-pointer">
                                                {
                                                    deletandoPessoa === presenteado.IdUsuario ? 'Removendo...' : 'Remover'
                                                }
                                            </span> */}
                                            </div>
                                        ))
                                    }
                                </div>
                            }

                            {
                                step !== 'selecionar' && (
                                    <div className="flex justify-center">
                                        <Button className="mt-3" onClick={handleBotaoVoltarSelecao}>Voltar para Seleção</Button>
                                    </div>
                                )
                            }

                            {
                                step === 'pesquisarcpf' && (
                                    <div className="grid gap-2 text-primary-foreground mt-5">
                                        <div>
                                            <Label className="text-center text-primary-foreground" htmlFor="cpf">CPF</Label>
                                            <InputMask
                                                disabled={buscandoPessoa}
                                                type="text"
                                                id="CPF"
                                                required
                                                formik={formik}
                                                name='CPF'
                                                mask={MASCARA_CPF}
                                            />
                                        </div>
                                    </div>
                                )
                            }

                            {
                                step === 'cadastrar' && (
                                    <form onSubmit={formik.handleSubmit} className="text-center text-lg mt-6">
                                        <div className="grid gap-1 text-primary-foreground">
                                            <Label className="text-center text-primary-foreground" htmlFor="cpf">CPF</Label>
                                            <InputMask
                                                disabled={buscandoPessoa || step === 'cadastrar'}
                                                type="text"
                                                id="CPF"
                                                required
                                                formik={formik}
                                                name='CPF'
                                                mask={MASCARA_CPF}
                                            />
                                        </div>

                                        <div className="grid gap-2 text-primary-foreground mt-3">
                                            <div className="flex items-center text-primary-foreground">
                                                <Label htmlFor="CEP">Digite seu CEP ou Selecione sua Cidade</Label>
                                            </div>
                                            <InputMask
                                                name="CEP"
                                                mask={MASCARA_CEP}
                                                placeholder="Cep"
                                                type="text"
                                                id="CEP"
                                                formik={formik}
                                            />

                                            <Select
                                                menuPlacement="top"
                                                defaultValue={{ value: '', label: 'Selecione sua Cidade' }}
                                                options={cidadesSelect}
                                                onChange={async (e) => {
                                                    const cidade = cidades.find(c => c.cidade === e!.value);
                                                    setEndereco({ Cidade: e!.value, CEP: '', UF: cidade!.estado, Bairro: '' });
                                                }}
                                                isMulti={false}
                                                className='form-control text-start text-sm' />
                                        </div>
                                        <div className="flex flex-col gap-1 mt-4">
                                            <Label className="text-center text-primary-foreground" htmlFor="nome">Nome Completo</Label>
                                            <InputFormik
                                                disabled={buscandoPessoa}
                                                type="text"

                                                id="Nome"
                                                required
                                                formik={formik}
                                                name='Nome'
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1 mt-4">
                                            <Label className="text-center text-primary-foreground" htmlFor="telefone">Telefone</Label>
                                            <InputMask
                                                type="text"
                                                id="Telefone"

                                                required
                                                formik={formik}
                                                name='Telefone'
                                                mask={MASCARA_TELEFONE}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1 mt-4">
                                            <Label className="text-center text-primary-foreground" htmlFor="email">Email</Label>
                                            <InputFormik
                                                disabled={buscandoPessoa}
                                                type="text"
                                                id="Email"
                                                formik={formik}
                                                name='Email'
                                            />
                                        </div>

                                        <Button
                                            disabled={formik.isSubmitting || !formik.isValid || endereco === undefined}
                                            variant="ghost"
                                            type="submit"
                                            className="w-full underline text-primary-foreground mt-2">
                                            {
                                                formik.isSubmitting ? 'Cadastrando...' : 'Cadastrar'
                                            }
                                        </Button>
                                    </form>
                                )
                            }
                        </div>
                    </ScrollArea>
                </DrawerContent>
            </Drawer>
        </div>
    )
}